import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Table, Breadcrumb, Form, Pagination } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faEye } from '@fortawesome/free-solid-svg-icons';

import AuthService from "../../services/auth.service";
import ElasticSearchService from "../../services/elasticSearch.service";
import ACService from "../../helpers/accessValidation.js";
import * as Constants from "../../components/constants";
import { personToStringWithId } from '../../helpers/util';

export default function PersonManagement() {
	const [persons, setPersons] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [errors, setErrors] = useState(null);
	const [successful, setSuccessful] = useState(false);
	const [message, setMessage] = useState(null);
	const [currentUser, setCurrentUser] = useState(null);
	const [filterText, setFilterText] = useState("");
	const [showWithoutTombstone, setShowWithoutTombstone] = useState(false);
	const [showWithoutCementry, setShowWithoutCementry] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(50);
	const [deletingPersonId, setDeletingPersonId] = useState(null);
	const navigate = useNavigate();

	useEffect(() => {
		const user = AuthService.getCurrentUser();
		
		// Prüfe ob Benutzer angemeldet ist
		if (!user) {
			navigate("/login");
			return;
		}
		
		// Prüfe ob Benutzer Administrator ist
		if (!ACService.accessValidation(user, Constants.AC_ADMIN_NAVIGATION)) {
			setErrors("Sie haben keine Berechtigung, diese Seite aufzurufen.");
			setIsLoading(false);
			return;
		}
		
		setCurrentUser(user);
		
		// Lade alle Personen
		ElasticSearchService.loadAllPersons().then(
			response => {
				setPersons(response.data || []);
				setIsLoading(false);
				setSuccessful(true);
			},
			error => {
				setErrors(
					(error.response && error.response.data) ||
					error.message ||
					error.toString()
				);
				setIsLoading(false);
				setSuccessful(false);
			}
		);
	}, [navigate]);
	
	
	// Filtere Personen basierend auf Filter-Text
	const filteredPersons = useMemo(() => {
		let result = persons;
		
		// Filtere nach Text
		if (filterText && filterText.trim() !== "") {
			const searchText = filterText.toLowerCase().trim();
			
			result = result.filter(person => {
				// Suche in Datenbank ID (konvertiere zu String für zuverlässige Suche)
				if (person.id) {
					const personIdStr = String(person.id).toLowerCase();
					if (personIdStr.includes(searchText)) {
						return true;
					}
				}
				
				// Suche in Personen-ID
				if (person.personId) {
					const personIdValueStr = String(person.personId).toLowerCase();
					if (personIdValueStr.includes(searchText)) {
						return true;
					}
				}
				
				// Suche in Name (personToStringWithId)
				const personName = personToStringWithId(person);
				if (personName && personName.toLowerCase().includes(searchText)) {
					return true;
				}
				
				// Suche in Friedhof
				if (person.cementry) {
					const cementryStr = String(person.cementry).toLowerCase();
					if (cementryStr.includes(searchText)) {
						return true;
					}
				}
				
				return false;
			});
		}
		
		// Filtere nach Personen ohne Grabsteinzuordnung
		if (showWithoutTombstone) {
			result = result.filter(person => {
				return !person.grabsteinnummer || person.grabsteinnummer.trim() === "";
			});
		}
		
		// Filtere nach Personen ohne Friedhofszuordnung
		if (showWithoutCementry) {
			result = result.filter(person => {
				return !person.cementry || person.cementry.trim() === "";
			});
		}
		
		return result;
	}, [persons, filterText, showWithoutTombstone, showWithoutCementry]);
	
	// Pagination: Berechne die angezeigten Personen für die aktuelle Seite
	const paginatedPersons = useMemo(() => {
		const startIndex = (currentPage - 1) * itemsPerPage;
		const endIndex = startIndex + itemsPerPage;
		return filteredPersons.slice(startIndex, endIndex);
	}, [filteredPersons, currentPage, itemsPerPage]);
	
	// Berechne die Gesamtzahl der Seiten
	const totalPages = useMemo(() => {
		return Math.ceil(filteredPersons.length / itemsPerPage);
	}, [filteredPersons.length, itemsPerPage]);
	
	// Reset zur ersten Seite wenn Filter geändert wird
	useEffect(() => {
		setCurrentPage(1);
	}, [filterText, showWithoutTombstone, showWithoutCementry]);

	// Löschfunktion für Personen
	const handleDeletePerson = async (person) => {
		if (!person || !person.id) {
			return;
		}

		// Bestätigungsdialog
		if (!window.confirm(`Möchten Sie die Person "${personToStringWithId(person)}" wirklich löschen?`)) {
			return;
		}

		try {
			setDeletingPersonId(person.id);
			
			// Prüfe ob Person gelöscht werden kann
			const checkResponse = await ElasticSearchService.checkPersonCanBeDeleted(person.id);
			
			if (checkResponse.data && checkResponse.data.canBeDeleted === false) {
				setMessage(checkResponse.data.reason || "Diese Person kann nicht gelöscht werden, da sie einem Grabstein zugeordnet ist.");
				setSuccessful(false);
				setDeletingPersonId(null);
				return;
			}

			// Person löschen
			await ElasticSearchService.deletePerson(person.id);
			
			// Personenliste aktualisieren
			const response = await ElasticSearchService.loadAllPersons();
			setPersons(response.data || []);
			setMessage("Person erfolgreich gelöscht.");
			setSuccessful(true);
			setDeletingPersonId(null);
		} catch (error) {
			const resMessage =
				(error.response && error.response.data && error.response.data.message) ||
				error.message ||
				error.toString();
			setMessage(resMessage);
			setSuccessful(false);
			setDeletingPersonId(null);
		}
	};
	
	// Prüfe ob Benutzer angemeldet und Administrator ist
	if (!currentUser || !ACService.accessValidation(currentUser, Constants.AC_ADMIN_NAVIGATION)) {
		return (
			<Container fluid="xxl">
				<Row>
					<Col>
						<Breadcrumb className="breadcrumb-style">
							<Breadcrumb.Item href="/home">Home</Breadcrumb.Item>
							<Breadcrumb.Item className="breadcrumb-item-style" active>Administration</Breadcrumb.Item>
							<Breadcrumb.Item className="breadcrumb-item-style" active>Personenübersicht</Breadcrumb.Item>
						</Breadcrumb>
					</Col>
				</Row>
				<Row>
					<Col>
						<h3>Personenübersicht</h3>
					</Col>
				</Row>
				<Row>
					<Col>
						<div className="alert alert-danger" role="alert">
							{errors || "Sie haben keine Berechtigung, diese Seite aufzurufen."}
						</div>
					</Col>
				</Row>
			</Container>
		);
	}
	
	const TableErrors = () => {
		if (!errors) return null;
		
		return (
			<tr>
				<td colSpan="6">
					<div className="form-group">
						<div className="alert alert-danger" role="alert">{errors}</div>
					</div>
				</td>
			</tr>
		);
	};
	
	const TableMessage = () => {
		if (!message) return null;
		
		return (
			<tr>
				<td colSpan="6">
					<div className="form-group">
						<div className={successful ? "alert alert-success" : "alert alert-danger"} role="alert">
							{message}
						</div>
					</div>
				</td>
			</tr>
		);
	};
	
	// Memoized TableItem Komponente für bessere Performance
	const TableItem = React.memo(({ person, onDelete }) => {
		// Memoize personToStringWithId Ergebnis - muss vor dem frühen Return sein
		const personName = useMemo(() => {
			if (!person) return "";
			return personToStringWithId(person) || "";
		}, [person]);
		
		if (!person) return null;
		
		// Extrahiere cementryId aus der person (falls vorhanden)
		let cementryId = person.cementry || "";
		
		// Prüfe ob Person gelöscht werden kann (keine grabsteinnummer)
		const canDelete = !person.grabsteinnummer || person.grabsteinnummer.trim() === "";
		
		return (
			<tr key={person.id}>
				<td>{person.id || ""}</td>
				<td>{person.personId || ""}</td>
				<td>{personName}</td>
				<td>{person.cementry || ""}</td>
				<td>
					{person.grabsteinnummer ? (
						person.cementry ? (
							<Link to={`/management/tombstone/${person.cementry}/${person.grabsteinnummer}`}>
								{person.grabsteinnummer}
							</Link>
						) : (
							<span>{person.grabsteinnummer}</span>
						)
					) : (
						<span className="text-muted">-</span>
					)}
				</td>
				<td>
					{person.id && cementryId ? (
						<>
							<Link 
								to={`/management/person/${cementryId}/${person.id}`} 
								className="me-3"
								title="Details anzeigen"
							>
								<FontAwesomeIcon icon={faEye} />
							</Link>
							{canDelete && (
								<button
									type="button"
									className="btn btn-link p-0 text-danger"
									onClick={(e) => {
										e.preventDefault();
										onDelete(person);
									}}
									disabled={deletingPersonId === person.id}
									title="Person löschen"
									style={{ textDecoration: 'none', border: 'none', background: 'none', cursor: deletingPersonId === person.id ? 'not-allowed' : 'pointer' }}
								>
									<FontAwesomeIcon icon={faTrash} />
								</button>
							)}
						</>
					) : (
						<span className="text-muted">-</span>
					)}
				</td>
			</tr>
		);
	});
	
	const TableItems = () => {
		return (
			<tbody>
				{!isLoading ? (
					paginatedPersons && paginatedPersons.length > 0 ? (
						paginatedPersons.map((person) => {
							return (
								<TableItem 
									key={person.id} 
									person={person}
									onDelete={handleDeletePerson}
								/>
							);
						})
					) : (
						<tr>
							<td colSpan="6" className="text-center">
								{filterText 
									? "Keine Personen gefunden, die dem Filter entsprechen" 
									: "Keine Personen gefunden"}
							</td>
						</tr>
					)
				) : (
					<tr>
						<td colSpan="6" className="text-center">Loading...</td>
					</tr>
				)}
				
				{errors && <TableErrors />}
				{message && <TableMessage />}
			</tbody>
		);
	};
	
	// Pagination Controls
	const PaginationControls = () => {
		if (totalPages <= 1) return null;
		
		const handlePageChange = (page) => {
			setCurrentPage(page);
			// Scroll nach oben beim Seitenwechsel
			window.scrollTo({ top: 0, behavior: 'smooth' });
		};
		
		const pages = [];
		const maxVisiblePages = 10;
		let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
		let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
		
		if (endPage - startPage < maxVisiblePages - 1) {
			startPage = Math.max(1, endPage - maxVisiblePages + 1);
		}
		
		for (let i = startPage; i <= endPage; i++) {
			pages.push(i);
		}
		
		return (
			<Pagination className="justify-content-center mt-3">
				<Pagination.First 
					onClick={() => handlePageChange(1)} 
					disabled={currentPage === 1}
				/>
				<Pagination.Prev 
					onClick={() => handlePageChange(currentPage - 1)} 
					disabled={currentPage === 1}
				/>
				{startPage > 1 && (
					<>
						<Pagination.Item onClick={() => handlePageChange(1)}>1</Pagination.Item>
						{startPage > 2 && <Pagination.Ellipsis />}
					</>
				)}
				{pages.map(page => (
					<Pagination.Item
						key={page}
						active={page === currentPage}
						onClick={() => handlePageChange(page)}
					>
						{page}
					</Pagination.Item>
				))}
				{endPage < totalPages && (
					<>
						{endPage < totalPages - 1 && <Pagination.Ellipsis />}
						<Pagination.Item onClick={() => handlePageChange(totalPages)}>
							{totalPages}
						</Pagination.Item>
					</>
				)}
				<Pagination.Next 
					onClick={() => handlePageChange(currentPage + 1)} 
					disabled={currentPage === totalPages}
				/>
				<Pagination.Last 
					onClick={() => handlePageChange(totalPages)} 
					disabled={currentPage === totalPages}
				/>
			</Pagination>
		);
	};
	
	return (
		<Container fluid="xxl">
			<Row>
				<Col>
					<Breadcrumb className="breadcrumb-style">
						<Breadcrumb.Item href="/home">Home</Breadcrumb.Item>
						<Breadcrumb.Item className="breadcrumb-item-style" active>Administration</Breadcrumb.Item>
						<Breadcrumb.Item className="breadcrumb-item-style" active>Personenübersicht</Breadcrumb.Item>
					</Breadcrumb>
				</Col>
			</Row>
			<Row>
				<Col><h3>Personenübersicht</h3></Col>
			</Row>
			
			<Row className="mb-3">
				<Col md={4}>
					<Form.Group>
						<Form.Label>Filter</Form.Label>
						<Form.Control
							type="text"
							placeholder="Nach Datenbank ID, Personen-ID, Name oder Friedhof suchen..."
							value={filterText}
							onChange={(e) => setFilterText(e.target.value)}
						/>
						<Form.Text className="text-muted">
							{filteredPersons.length} von {persons.length} Personen angezeigt
						</Form.Text>
					</Form.Group>
				</Col>
				<Col md={4}>
					<Form.Group>
						<Form.Check
							type="checkbox"
							label="Nur Personen ohne Grabsteinzuordnung anzeigen"
							checked={showWithoutTombstone}
							onChange={(e) => setShowWithoutTombstone(e.target.checked)}
						/>
						<Form.Text className="text-muted">
							{showWithoutTombstone 
								? "Es werden nur Personen ohne Grabsteinzuordnung angezeigt"
								: "Alle Personen werden angezeigt"}
						</Form.Text>
					</Form.Group>
					<Form.Group className="mt-2">
						<Form.Check
							type="checkbox"
							label="Nur Personen ohne Friedhofszuordnung anzeigen"
							checked={showWithoutCementry}
							onChange={(e) => setShowWithoutCementry(e.target.checked)}
						/>
						<Form.Text className="text-muted">
							{showWithoutCementry 
								? "Es werden nur Personen ohne Friedhofszuordnung angezeigt"
								: "Alle Personen werden angezeigt"}
						</Form.Text>
					</Form.Group>
				</Col>
				<Col md={4}>
					<Form.Group>
						<Form.Label>Einträge pro Seite</Form.Label>
						<Form.Select
							value={itemsPerPage}
							onChange={(e) => {
								setItemsPerPage(Number(e.target.value));
								setCurrentPage(1);
							}}
						>
							<option value={25}>25</option>
							<option value={50}>50</option>
							<option value={100}>100</option>
							<option value={200}>200</option>
						</Form.Select>
						<Form.Text className="text-muted">
							Seite {currentPage} von {totalPages}
						</Form.Text>
					</Form.Group>
				</Col>
			</Row>
			
			<Table striped bordered hover>
				<thead>
					<tr>
						<th>Datenbank ID</th>
						<th>Personen-ID</th>
						<th>Name</th>
						<th>Friedhof</th>
						<th>Grabsteinzuordnung</th>
						<th>Aktionen</th>
					</tr>
				</thead>
				<TableItems />
			</Table>
			
			<PaginationControls />
		</Container>
	);
}

